import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useHistory, useLocation, useParams} from "react-router";
import {nanoid} from "@reduxjs/toolkit";
import Card from "@material-ui/core/Card";
import {CardHeader} from "@material-ui/core";
import CardContent from "@material-ui/core/CardContent";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Typography from "@material-ui/core/Typography";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionActions from "@material-ui/core/AccordionActions";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import {useConfirm} from "material-ui-confirm";
import { isEmpty, uniqBy } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import ProcessingIndicator from '../../../Common/ProcessingIndicator';
import {
    getAllParkers,
    getBillingProfile,
    getFacilityInfo,
    getPaymentData,
    getPayorData,
    getSubscriptionIdValue,
    hasReachedCheckout,
    removeParker,
} from "../../../../Store/purchase.slice";
import { fetchBillingSummary } from "../../../../Store/rates.slice";
import SummaryData from "./SummaryData";

function CartInfo({
    confirmOnRedirect,
    addedAccessDevices,
    currentParkerId,
    selectedRateId,
    rateStartDate,
}) {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const confirm = useConfirm();
    const reachedCheckout = useSelector(hasReachedCheckout)
    const paymentData = useSelector(getPaymentData);
    const billingProfile = useSelector(getBillingProfile);
    const subscriptionId = useSelector(getSubscriptionIdValue);
    const {facility_name, address} = useSelector(getFacilityInfo);
    const {parkerId} = useParams();
    const payorData = useSelector(getPayorData);
    const allParkersList = useSelector(getAllParkers);
    const allParkers = useMemo(() => {
        const parkers = allParkersList.map(p => {
            return p.id !== currentParkerId ? p : {
                ...p,
                rateId: !!p.rateId ? p.rateId : selectedRateId,
                startDate: !!rateStartDate ? rateStartDate : p.startDate,
                accessDevices: !!addedAccessDevices ? addedAccessDevices : p.accessDevices,
            };
        })
        // this adds the unsaved parker to the parking charges
        // a parker will be in an unsaved state when selectedRateId
        // is not null.
        if (currentParkerId && selectedRateId) {
            if (!parkers.length) {
                return [{id: currentParkerId, rateId: selectedRateId, accessDevices: []}]
            } else {
                parkers.push({id: currentParkerId, rateId: selectedRateId, accessDevices: []})
            }
        }
        return parkers;
    }, [addedAccessDevices, allParkersList, currentParkerId, rateStartDate, selectedRateId])

    const [billingSummary, setBillingSummary] = useState();
    const [loading, setLoading] = useState(true);

    const recurringFeeTypes = ['MONTHLY'];

    const {
        payment_due_day: paymentDueDay,
        billing_day: billingDay,
        secondMonthPayment,
    } = billingProfile;

    let initialGroups = [
        { label: 'Parker Setup Fees', types: ['ACTIVATION', 'ACCESS_DEVICE'] },
        { label: 'Prorated Recurring Fees', types: recurringFeeTypes },
    ];

    const showJumpToCheckout = reachedCheckout && !location.pathname.startsWith('/checkout')
    const showParkerControls = !['/parker', '/rates'].some(match => location.pathname.startsWith(match));

    const handleDeleteParkerClick = useCallback((id) => {
        confirm({
            title: 'Delete Confirmation!',
            confirmationText: 'DELETE',
            cancellationText: 'Cancel',
            description: 'The action you are about to take cannot be undone. Are you sure you want to continue?'
        }).then(() => dispatch(removeParker({id}))).catch(() => console.log('Keeping data'));
    }, [dispatch, confirm]);

    const unSavedDialogConfig = {
        title: 'Unsaved Changes!',
        confirmationText: 'Continue',
        cancellationText: 'Stay On Page',
        description: 'There are unsaved changes on your page. Are you sure you want to continue?'
    };

    const redirectOnConfirm = useCallback(path => {
        if (!!confirmOnRedirect) {
            confirm(unSavedDialogConfig).then(() => {
                history.push(path)
            }).catch(() => console.log('Staying on page...'));
        } else {
            history.push(path)
        }
    }, [confirmOnRedirect, history, confirm, unSavedDialogConfig]);

    useEffect(() => {
        (async () => {
            if (!allParkers.length) return;
            setLoading(true);

            const parkers = allParkers.map(({ id, rateId, startDate, accessDevices, first_name, last_name }) => ({
                id,
                name: `${first_name ?? ''} ${last_name ?? ''}`,
                rateId,
                startDate: startDate ?? moment().format('YYYY-MM-DD'),
                accessDevices,
            }));
            const uniqueParkers = uniqBy(parkers, 'id'); // TODO: Fix "Back" button adding duplicate parkers

            const creditCardSelected = paymentData.type === 'cc';

            const { payload } = await dispatch(fetchBillingSummary({
                parkers: uniqueParkers,
                subscriptionId,
                includeCreditCardFees: creditCardSelected,
            }));

            setBillingSummary(payload);
            setLoading(false);
        })();
    }, [dispatch, allParkers, subscriptionId, paymentData]);

    if (loading) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'end', 'margin-top': '60%'}}>
                <ProcessingIndicator/>
            </div>
        );
    }

    return <Card>
        <CardHeader title={facility_name} subheader={address} 
            titleTypographyProps={{"data-cy": "facility-name-card-header-text"}}
            subheaderTypographyProps={{"data-cy": "facility-address-card-header-text"}}
        />
        <CardContent>
            <SummaryData
                expanded
                label='Billing Summary'
                type='billing'
                recurring={billingSummary.totals.recurring}
                initial={billingSummary.totals.initial}
                paymentDay={paymentDueDay}
                billingDay={billingDay}
                secondMonthPayment={secondMonthPayment}
                parkers={allParkers}
                actions={[
                    showJumpToCheckout ? {
                        label: 'Go To Checkout',
                        isPrimary: true,
                        onClick: () => redirectOnConfirm(`/checkout`)
                    } : null,
                    showParkerControls ? {
                        label: 'Add New Parker',
                        isPrimary: !showJumpToCheckout,
                        onClick: () => redirectOnConfirm(`/rates/${nanoid()}`)
                    } : null
                ].filter(action => !isEmpty(action))}
            />
            {!selectedRateId && billingSummary.parkers.map(({ id, name, initial, recurring, startDate }, i) => (
                <SummaryData
                    key={i}
                    label={name}
                    initial={{...initial, groups: initialGroups}}
                    recurring={recurring}
                    startDate={startDate}
                    expanded={parkerId === id && allParkers.length > 1}
                    paymentDay={paymentDueDay}
                    billingDay={billingDay}
                    secondMonthPayment={secondMonthPayment}
                    type='parker'
                    actions={showParkerControls ?
                        [
                            {
                                label: 'Delete',
                                isPrimary: false,
                                disabled: parkerId === id || billingSummary.parkers.length === 1,
                                onClick: () => handleDeleteParkerClick(id)
                            }, {
                                label: 'Modify',
                                isPrimary: true,
                                disabled: parkerId === id ,
                                onClick: () => redirectOnConfirm(`/rates/${id}`),
                            }
                        ] : []
                    }
                />
            ))}
            {!isEmpty(payorData) ? <Accordion key="payment-information">
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Box display="flex" flexDirection="column">
                        <Box display="flex" flexDirection="row" width="100%">
                            <Typography variant='subtitle1'>Payment Information</Typography>
                        </Box>
                        <Box display="flex" flexDirection="row" width="100%">
                            <Typography variant='caption'>
                                {payorData.type === 'INDIVIDUAL' ? 'Individual Account' : 'Business Account'}
                            </Typography>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box display="flex" flexDirection="column">
                        <Box display="flex" flexDirection="row" width="100%">
                            <Typography variant='subtitle1'>
                                {payorData.billing_firstname} {payorData.billing_lastname}
                            </Typography>
                        </Box>
                        <Box display="flex" flexDirection="row" width="100%">
                            <Typography variant='subtitle1'>{payorData.billing_street}</Typography>
                        </Box>
                        <Box display="flex" flexDirection="row" width="100%">
                            <Typography variant='subtitle1'>
                                {payorData.billing_city}, {payorData.billing_state}, {payorData.billing_zip}
                            </Typography>
                        </Box>
                    </Box>
                </AccordionDetails>
                <AccordionActions>
                    <Button size="small" color="primary" onClick={() => {
                        redirectOnConfirm('payor')
                    }}>Modify</Button>
                </AccordionActions>
            </Accordion> : ''}
        </CardContent>
    </Card>
}
CartInfo.propTypes = {
    rateStartDate: PropTypes.string,
    selectedRateId: PropTypes.string,
    currentParkerId: PropTypes.string,
    confirmOnRedirect: PropTypes.bool,
    addedAccessDevices: PropTypes.arrayOf(PropTypes.string),
}
export default React.memo(CartInfo);
